import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { useEffect, useState } from 'react';

import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { DatoSpeaker } from '@src/types.ts/story';

const logger = LogManager.getLogger('content:useSectionSpeakers');

/**
 * PLAT-11051 — resolves the transcript's internal `spk_X` labels to the people
 * they were identified as, so a Section row can show faces instead of a raw
 * label.
 *
 * Only identified speakers resolve. A label with no Dato speaker behind it is
 * absent from the map, which is what lets the row hide the face pile entirely
 * rather than render an anonymous placeholder.
 *
 * Fetched once per story; both underlying fetches are themselves cached.
 */
export function useSectionSpeakers(
  storyId: string | undefined,
  datoClientStore: DatoClientStore | undefined,
): Map<string, DatoSpeaker> {
  const [speakersByLabel, setSpeakersByLabel] = useState<
    Map<string, DatoSpeaker>
  >(() => new Map());

  useEffect(() => {
    if (!storyId || !datoClientStore) {
      setSpeakersByLabel(new Map());
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const [mappings, datoSpeakers] = await Promise.all([
          fetchSpeakerVoiceMappings(storyId),
          fetchDatoSpeakers(storyId, datoClientStore),
        ]);
        if (cancelled) return;

        // `fetchDatoSpeakers` returns the loose shape (name/title optional);
        // the face pile wants the story one. Unnamed speakers count as
        // unidentified — there is no face to show — so they drop out here.
        const byId = new Map<string, DatoSpeaker>(
          datoSpeakers
            .filter(speaker => !!speaker.name)
            .map(speaker => [
              speaker.id,
              {
                id: speaker.id,
                name: speaker.name!,
                title: speaker.title ?? '',
                headshots: speaker.headshots,
              },
            ]),
        );
        const resolved = new Map<string, DatoSpeaker>();

        for (const [label, datoSpeakerId] of Object.entries(
          mappings?.mappings ?? {},
        )) {
          const speaker = byId.get(datoSpeakerId);
          if (speaker) resolved.set(label, speaker);
        }

        // `override_<id>` labels encode the Dato speaker in the label itself.
        for (const speaker of byId.values()) {
          resolved.set(`override_${speaker.id}`, speaker);
        }

        setSpeakersByLabel(resolved);
      } catch (error) {
        if (cancelled) return;
        logger.warn('Failed to resolve section speakers', {
          storyId,
          ...extractErrorInfo(error),
        });
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [storyId, datoClientStore]);

  return speakersByLabel;
}
